import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { Star, Package, ArrowLeft, Store } from "lucide-react";

const ShopPage = () => {
    const { sellerId } = useParams();
    const [seller, setSeller] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch seller profile
                const profileRes = await axios.get(`${API_BASE_URL}/api/sellers/${sellerId}/profile`);
                setSeller(profileRes.data);

                // Fetch seller products
                const productsRes = await axios.get(`${API_BASE_URL}/api/sellers/${sellerId}/products`);
                setProducts(productsRes.data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [sellerId]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100">
                <div className="max-w-7xl mx-auto px-4 py-12">
                    <div className="animate-pulse">
                        <div className="h-40 bg-gray-200 rounded-xl mb-8" />
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="bg-gray-200 rounded-xl aspect-square" />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">ไม่พบร้านค้า</h2>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <Link to="/" className="text-red-600 hover:text-red-700">
                        กลับหน้าหลัก
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Back Button */}
            <div className="bg-white border-b">
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <button
                        onClick={() => window.history.back()}
                        className="inline-flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors"
                    >
                        <ArrowLeft size={20} />
                        ย้อนกลับ
                    </button>
                </div>
            </div>

            {/* Shop Header */}
            <div className="bg-black text-white">
                <div className="max-w-7xl mx-auto px-4 py-12">
                    <div className="flex items-center gap-6">
                        {/* Shop Avatar */}
                        <div className="w-24 h-24 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center">
                            <Store className="w-12 h-12 text-white" />
                        </div>

                        {/* Shop Info */}
                        <div className="flex-1">
                            <h1 className="text-3xl font-bold mb-2 text-white">{seller?.shopName || "ชื่อร้านค้า"}</h1>
                            <div className="flex flex-wrap items-center gap-6 text-white/80">
                                {/* Rating */}
                                <div className="flex items-center gap-1">
                                    <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                                    <span className="font-semibold text-white">
                                        {seller?.rating?.toFixed(1) || "0.0"}
                                    </span>
                                    <span className="text-white/60">
                                        ({seller?.reviewCount || 0} รีวิว)
                                    </span>
                                </div>
                                {/* Product Count */}
                                <div className="flex items-center gap-1">
                                    <Package className="w-5 h-5" />
                                    <span>{seller?.productCount || 0} สินค้า</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Products Grid */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                    {/* Header */}
                    <div className="px-6 py-5 border-b border-gray-200">
                        <h2 className="text-xl font-bold text-gray-900">สินค้าทั้งหมดของร้าน</h2>
                    </div>

                    {/* Products */}
                    <div className="p-6">
                        {products.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                                <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                                <p>ร้านนี้ยังไม่มีสินค้า</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                                {products.map((product) => (
                                    <Link
                                        key={product.productId}
                                        to={`/product-details/${product.productId}`}
                                        className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all"
                                    >
                                        <div className="aspect-square bg-gray-100 overflow-hidden">
                                            <img
                                                src={product.imageUrl ? `${API_BASE_URL}${product.imageUrl}` : "https://via.placeholder.com/300?text=No+Image"}
                                                alt={product.productName}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                onError={(e) => { e.target.src = "https://via.placeholder.com/300?text=No+Image"; }}
                                            />
                                        </div>
                                        <div className="p-4">
                                            <h3 className="font-medium text-gray-900 truncate group-hover:text-red-600 transition-colors">
                                                {product.productName}
                                            </h3>
                                            <p className="text-lg font-bold text-red-600 mt-1">
                                                ฿{Number(product.price).toLocaleString()}
                                            </p>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShopPage;
